import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import {
    Box, Typography, Tab, Tabs, FormControl, InputLabel, Select,
    MenuItem, Checkbox, ListItemText, Button, CircularProgress, Tooltip, Skeleton,
    ListSubheader, InputAdornment, TextField
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useMachineGroups } from '../../Shared/hooks/useMachineGroups';
import PlantFilterSelect from '../../Shared/components/PlantFilterSelect';
import { useUserRole } from '../../Shared/hooks/useUserRole';
import { customerbasedshift, cleanCustomerId, telemetrykeydata } from '../../Services/app/operatorservice';
import { fetchPartsCountStatusSummary } from '../../Shared/utils/partsdata';
import { getAlarmByPeriod, getAlarmByPeriodTop, getMetricByPeriod, getPartsAnalytics, getIdleByPeriod, getIdleReasonByPeriod, getEshLhByPeriod } from '../../Services/app/analyticsservice';
import { mergeReasonsForPlant, mergeBreakdownSeries } from '../../Shared/utils/analyticsPlantMerge';
import './AnalyticsV2.css';

dayjs.extend(isoWeek);
dayjs.extend(utc);
dayjs.extend(timezone);

const ALL_TABS = [
    { value: 'utilization', label: 'Utilization' },
    { value: 'oee', label: 'OEE' },
    { value: 'parts', label: 'Parts' },
    { value: 'parts_summary', label: 'Parts Summary', restrictedToCustomer: true, hidden: true },
    { value: 'cycle_time', label: 'Cycle Time', restrictedToCustomer: true, hidden: true },
    { value: 'downtime', label: 'Downtime' },
    { value: 'alarm', label: 'Alarm' },
    { value: 'esh_lh', label: 'ESH/LH', pmiOnly: true },
];

const VIEW_OPTS = [{ value: 'day', label: 'Day' }, { value: 'week', label: 'Week' }, { value: 'month', label: 'Month' }, { value: 'quarter', label: 'Quarter' }];
const PERIOD_OPTS = [{ value: 'day', label: 'Day' }, { value: 'week', label: 'Week' }, { value: 'month', label: 'Month' }, { value: 'quarter', label: 'Quarter' }];
const TOP_N_OPTS = [5, 10, 15, 20];

const BAR_COLORS = [
    '#e53935', '#1e88e5', '#43a047', '#8e24aa', '#e64a19',
    '#6d9eeb', '#f4b942', '#e91e63', '#00897b', '#5c6bc0',
    '#ff7043', '#26a69a', '#ab47bc', '#42a5f5', '#ef5350',
    '#66bb6a', '#ffa726', '#26c6da', '#ec407a', '#7e57c2',
];

const BLUECARD_COLORS = [
    '#ff6b35', '#7b2d8b', '#00b4d8', '#2dc653', '#ffbe0b',
    '#fb5607', '#3a86ff', '#8338ec', '#06d6a0', '#ef233c',
    '#f72585', '#4cc9f0', '#4361ee', '#7209b7', '#f3722c',
];

const chartToolbar = (filename) => ({
    show: true,
    tools: { download: true, selection: false, zoom: false, zoomin: false, zoomout: false, pan: false, reset: false },
    export: {
        csv: { filename },
        svg: { filename },
        png: { filename },
    },
});

const AUTO_LOAD_TABS = ['utilization', 'oee', 'parts', 'parts_summary', 'cycle_time', 'downtime', 'alarm', 'esh_lh'];
// ESH/LH: how many periods (days/weeks/…) to show per chart+table page, so a
// long date range paginates instead of cramming every bar into one chart.
const ESH_PERIODS_PER_PAGE = 10;

const secsToHours = s => +(s / 3600).toFixed(2);
const formatH = s => {
    const h = s / 3600;
    if (h >= 1) return `${h.toFixed(2)} h`;
    const m = s / 60;
    if (m >= 1) return `${m.toFixed(0)}m ${s % 60}s`;
    return `${s}s`;
};

export default function AnalyticsV2() {
    const customerId = localStorage.getItem('CustomerID');
    const { isSuperAdmin } = useUserRole();
    const envConfig = window._env_ || {};
    const TABS = ALL_TABS.filter(t => !t.hidden).filter(t => (!t.restrictedToCustomer || isSuperAdmin) && (!t.pmiOnly || cleanCustomerId(customerId) === envConfig.CUSTOMER_ID));

    const {
        deviceNameID, machineGroups, availableMachines, selectedMachines, selectedGroups,
        showMachineGroupsDropdown, isAllMachinesSelected,
        handleGroupChange, handleMachineChange, setSelectedMachines,
        plantOptions, selectedPlants, showPlantFilter, handlePlantChange,
    } = useMachineGroups(customerId);

    const [tab, setTab] = useState(() => TABS[0]?.value || 'utilization');
    const [machineDropdownSearch, setMachineDropdownSearch] = useState("");
    const [shifts, setShifts] = useState([]);
    const [selectedShift, setSelectedShift] = useState([]);
    const [fromDate, setFromDate] = useState(dayjs().subtract(7, 'day'));
    const [toDate, setToDate] = useState(dayjs());

    const [oeeView, setOeeView] = useState('day');
    const [utilView, setUtilView] = useState('day');
    const [eshLhView, setEshLhView] = useState('day');
    const [eshLhExpanded, setEshLhExpanded] = useState({}); // { [periodIndex]: true } — expanded trend rows
    const [eshLhPage, setEshLhPage] = useState(0); // current page of ESH/LH periods (chart + table)
    const [partsOpen, setPartsOpen] = useState({}); // { [`${machineIdx}-${componentIdx}-${bucketKey}`]: true } — expanded part-wise breakdowns

    const [partsFromDate, setPartsFromDate] = useState(dayjs().startOf('year'));
    const [partsToDate, setPartsToDate] = useState(dayjs());

    const [summaryDate, setSummaryDate] = useState(dayjs());
    const [summaryShift, setSummaryShift] = useState('');

    const [cycleDate, setCycleDate] = useState(dayjs());
    const [cycleShift, setCycleShift] = useState('');
    const [cycleMachine, setCycleMachine] = useState('');
    const [cycleComponent, setCycleComponent] = useState('all');
    const [availableComponents, setAvailableComponents] = useState([]);

    const [period, setPeriod] = useState('day');
    const [topBars, setTopBars] = useState(20);
    const [topCount, setTopCount] = useState(3);

    const [isLoading, setIsLoading] = useState(false);
    const [chartData, setChartData] = useState(null);
    useEffect(() => { setEshLhPage(0); }, [chartData]); // reset to first page whenever new data loads
    const [iframeUrl, setIframeUrl] = useState('');
    const [expandedOthers, setExpandedOthers] = useState(false);
    const [selectedReason, setSelectedReason] = useState(null);

    const needsAutoLoadRef = useRef(true);
    const requestIdRef = useRef(0);

    useEffect(() => {
        if (!customerId) return;
        customerbasedshift(customerId, 'allShift').then(res => {
            const list = res?.[0]?.value || [];
            setShifts(list);
            if (list.length > 0) {
                setSummaryShift(list[0].shift_no);
                setCycleShift(list[0].shift_no);
                setSelectedShift(list.map(s => s.shift_no));
            }
        }).catch(console.error);
    }, [customerId]);

    useEffect(() => {
        if (tab === 'cycle_time' && availableMachines.length > 0 && !cycleMachine)
            setCycleMachine(availableMachines[0]);
    }, [tab, availableMachines, cycleMachine]);

    // Fetch available components for cycle time whenever machine/date/shift changes
    useEffect(() => {
        if (tab !== 'cycle_time' || !cycleMachine || !cycleDate || !cycleShift || shifts.length === 0) return;
        const deviceId = deviceNameID.find(d => d.name === cycleMachine)?.id;
        if (!deviceId) return;
        const shiftObj = shifts.find(s => String(s.shift_no) === String(cycleShift)) || shifts[0];
        if (!shiftObj) return;
        const [startH, startM] = shiftObj.start_time.split(':').map(Number);
        const [endH, endM] = shiftObj.end_time.split(':').map(Number);
        const fromDT = cycleDate.hour(startH).minute(startM).second(0).millisecond(0);
        let toDT = cycleDate.hour(endH).minute(endM).second(0).millisecond(0);
        if (endH < startH || (endH === startH && endM <= startM)) toDT = toDT.add(1, 'day');
        telemetrykeydata(deviceId, 'DEVICE', 'live_component', fromDT.valueOf(), toDT.valueOf())
            .then(res => {
                const compSet = new Set();
                const comps = [];
                (res?.live_component || []).forEach(item => {
                    try {
                        const p = JSON.parse(item.value);
                        if (p?.name && p?.code) {
                            const key = `${p.name}-${p.code}`;
                            if (!compSet.has(key)) { compSet.add(key); comps.push({ name: p.name, code: p.code }); }
                        }
                    } catch { }
                });
                setAvailableComponents(comps);
                setCycleComponent('all');
            })
            .catch(() => setAvailableComponents([]));
    }, [tab, cycleMachine, cycleDate, cycleShift, shifts, deviceNameID]); // eslint-disable-line react-hooks/exhaustive-deps

    // Mark that a fresh auto-load is needed whenever the tab changes
    useEffect(() => { needsAutoLoadRef.current = true; }, [tab]);

    // Execute auto-load once machines + shifts (+ cycleMachine for cycle_time) are ready
    useEffect(() => {
        if (!needsAutoLoadRef.current) return;
        if (!AUTO_LOAD_TABS.includes(tab)) return;
        if (selectedMachines.length === 0 || shifts.length === 0) return;
        if (tab === 'cycle_time' && !cycleMachine) return; // wait until cycleMachine is set
        needsAutoLoadRef.current = false;
        handleRunAnalysis(); // eslint-disable-line react-hooks/exhaustive-deps
    }, [tab, selectedMachines, shifts, cycleMachine]); // eslint-disable-line react-hooks/exhaustive-deps

    const isShiftDisabled = useMemo(() => {
        if (['parts_summary', 'cycle_time', 'parts', 'downtime', 'alarm', 'oee', 'utilization', 'esh_lh'].includes(tab)) return false;
        return !fromDate || !toDate ? true : !fromDate.isSame(toDate, 'day');
    }, [tab, fromDate, toDate]);

    const isRunDisabled = useMemo(() => {
        if (tab === 'cycle_time') return !cycleMachine || !cycleDate || !cycleShift;
        return selectedMachines.length === 0;
    }, [tab, selectedMachines, cycleMachine, cycleDate, cycleShift]);

    const isAllShiftsSelected = shifts.length > 0 && selectedShift.length === shifts.length;

    const handleTabChange = (_, newTab) => { setTab(newTab); setSelectedShift(shifts.map(s => s.shift_no)); setChartData(null); setIframeUrl(''); setExpandedOthers(false); setSelectedReason(null); };

    const handleRunAnalysis = async () => {
        const reqId = ++requestIdRef.current;

        const machines = selectedMachines.join(',');
        const shiftParam = (selectedShift.length === 0 || selectedShift.length === shifts.length)
            ? shifts.map(s => s.shift_no).join(',')
            : selectedShift.join(',');
        const from = fromDate.format('YYYY-MM-DD');
        const to = toDate.format('YYYY-MM-DD');
        const allShifts = shifts.map(s => s.shift_no).join(',');

        setIsLoading(true);
        setChartData(null);
        setIframeUrl('');
        setExpandedOthers(false);
        setSelectedReason(null);
        try {
            let res;
            if (tab === 'parts_summary') {
                const machineNames = selectedMachines.length ? [selectedMachines[0]] : [];
                const deviceIds = machineNames.map(n => deviceNameID.find(d => d.name === n)?.id).filter(Boolean);
                const shiftObj = shifts.find(s => String(s.shift_no) === String(summaryShift)) || shifts[0];
                if (deviceIds.length && shiftObj) {
                    const [sH, sM] = shiftObj.start_time.split(':').map(Number);
                    const [eH, eM] = shiftObj.end_time.split(':').map(Number);
                    const fromDT = summaryDate.hour(sH).minute(sM).second(0).millisecond(0);
                    let toDT = summaryDate.hour(eH).minute(eM).second(0).millisecond(0);
                    if (eH < sH || (eH === sH && eM <= sM)) toDT = toDT.add(1, 'day');
                    const psData = await fetchPartsCountStatusSummary({ deviceIds, fromEpoch: fromDT.valueOf(), toEpoch: toDT.valueOf() });
                    if (reqId !== requestIdRef.current) return;
                    const psBase = `${window._env_.GRAFANA_URL}d/cfhyiqoqbsc8we/analytics-parts-summary-chart?orgId=1&kiosk&theme=light`;
                    setIframeUrl(`${psBase}&var-partsummary=${encodeURIComponent(JSON.stringify(psData))}`);
                    // res stays undefined — iframeUrl drives the render
                }
            } else if (tab === 'cycle_time') {
                const deviceId = deviceNameID.find(d => d.name === cycleMachine)?.id;
                const shiftObj = shifts.find(s => String(s.shift_no) === String(cycleShift)) || shifts[0];
                if (deviceId && shiftObj) {
                    const [sH, sM] = shiftObj.start_time.split(':').map(Number);
                    const [eH, eM] = shiftObj.end_time.split(':').map(Number);
                    const fromDT = cycleDate.hour(sH).minute(sM).second(0).millisecond(0);
                    let toDT = cycleDate.hour(eH).minute(eM).second(0).millisecond(0);
                    if (eH < sH || (eH === sH && eM <= sM)) toDT = toDT.add(1, 'day');
                    const ctData = await fetchPartsCountStatusSummary({ deviceIds: [deviceId], fromEpoch: fromDT.valueOf(), toEpoch: toDT.valueOf() });
                    if (reqId !== requestIdRef.current) return;
                    const ctBase = `${window._env_.GRAFANA_URL}d/afi212e1xb7k0b/analytics-cycle-comparison-chart?orgId=1&kiosk&theme=light`;
                    const compValue = cycleComponent === 'all'
                        ? availableComponents.map(c => c.name).join(',')
                        : cycleComponent;
                    const compParam = compValue ? `&var-component=${encodeURIComponent(compValue)}` : '';
                    setIframeUrl(`${ctBase}&var-partsummary=${encodeURIComponent(JSON.stringify(ctData))}${compParam}`);
                }
            } else if (tab === 'downtime') {
                // Step 1: all idle reasons summary
                const summary = await getIdleByPeriod(machines, shiftParam, from, to);
                const totalSecs = summary.total_idle_duration || 0;

                // Step 2: fetch plant master reasons
                let masterReasons = [];
                try {
                    const rawMaster = customerId ? await customerbasedshift(customerId, 'reason') : null;
                    const rawVal = rawMaster?.[0]?.value;
                    masterReasons = typeof rawVal === 'string' ? JSON.parse(rawVal) : (Array.isArray(rawVal) ? rawVal : []);
                } catch (e) {
                    masterReasons = [];
                }

                // Step 3: concept-keyed plant merge
                const isAllPlants = !selectedPlants || selectedPlants.length === 0 || (plantOptions.length > 0 && selectedPlants.length === plantOptions.length);
                const effectivePlants = isAllPlants ? [] : selectedPlants;
                const mergeResult = mergeReasonsForPlant(
                    summary.idleReasonWithPercentage || [],
                    effectivePlants,
                    masterReasons
                );

                // Step 4: registered reasons list (sorted by duration)
                const registeredReasons = (mergeResult.mergedReasons || [])
                    .filter(r => (r.name || '').toLowerCase() !== 'others')
                    .map(r => ({
                        name: r.name,
                        total_duration_seconds: r.total_duration_seconds,
                        rawAliases: r.rawAliases,
                    }))
                    .sort((a, b) => b.total_duration_seconds - a.total_duration_seconds);

                let cumSum = 0;
                const reasons = registeredReasons.map(r => {
                    cumSum += r.total_duration_seconds;
                    return {
                        name: r.name,
                        total_duration_seconds: r.total_duration_seconds,
                        cumPct: totalSecs > 0 ? +((cumSum / totalSecs) * 100).toFixed(2) : 0,
                        rawAliases: r.rawAliases,
                    };
                });

                // Step 5: period breakdown for top N reasons (querying all raw aliases in parallel and merging)
                const topPick = registeredReasons.slice(0, topCount);

                const breakdowns = await Promise.all(
                    topPick.map(async (r, idx) => {
                        const aliasesToFetch = (r.rawAliases && r.rawAliases.length > 0) ? r.rawAliases : [r.name];
                        try {
                            const aliasResponses = await Promise.all(
                                aliasesToFetch.map(aliasName =>
                                    getIdleReasonByPeriod(machines, shiftParam, from, to, period, aliasName).catch(() => ({ data: [] }))
                                )
                            );
                            const mergedPoints = mergeBreakdownSeries(aliasResponses, period);
                            return {
                                name: r.name,
                                rank: idx + 1,
                                total_duration_seconds: r.total_duration_seconds,
                                data: mergedPoints,
                                aliases: r.rawAliases,
                            };
                        } catch {
                            return {
                                name: r.name,
                                rank: idx + 1,
                                total_duration_seconds: r.total_duration_seconds,
                                data: [],
                                aliases: r.rawAliases,
                            };
                        }
                    })
                );

                res = {
                    reasons,
                    total_duration_seconds: totalSecs,
                    unassigned_others_seconds: mergeResult.unassignedOthersSecs || 0,
                    breakdown: breakdowns,
                    bluecard: summary.bluecard || []
                };
            } else if (tab === 'alarm') {
                const alarmSummary = await getAlarmByPeriod(machines, shiftParam, from, to);
                const alarmSorted = (alarmSummary.alarmWithPercentage || [])
                    .map(r => ({ ...r, name: r.alarm_message?.trim() || '' }))
                    .sort((a, b) => b.total_alarm_duration - a.total_alarm_duration);
                const alarmTotal = alarmSummary.total_alarm_duration || 0;
                let alarmCum = 0;
                const alarmReasons = alarmSorted.map(r => {
                    alarmCum += r.total_alarm_duration;
                    return { name: r.name, total_duration_seconds: r.total_alarm_duration, cumPct: alarmTotal > 0 ? +((alarmCum / alarmTotal) * 100).toFixed(2) : 0 };
                });
                const alarmTop = alarmSorted.slice(0, topCount);
                const alarmBreakdowns = await Promise.all(alarmTop.map((r, idx) =>
                    getAlarmByPeriodTop(machines, shiftParam, from, to, period, r.name)
                        .then(bRes => ({
                            name: r.name, rank: idx + 1, total_duration_seconds: r.total_alarm_duration,
                            data: (bRes.data || [])
                                .sort((a, b) => new Date(a.period_start) - new Date(b.period_start))
                                .map(d => ({ period: fmtPeriodLabel(d.period_start, period), duration_seconds: d.total_alarm_duration })),
                        }))
                        .catch(() => ({ name: r.name, rank: idx + 1, total_duration_seconds: r.total_alarm_duration, data: [] }))
                ));
                res = { reasons: alarmReasons, total_duration_seconds: alarmTotal, breakdown: alarmBreakdowns, bluecard: [] };
            } else if (tab === 'parts') {
                res = await getPartsAnalytics(machines, allShifts, partsFromDate.format('YYYY-MM-DD'), partsToDate.format('YYYY-MM-DD'));
            } else if (tab === 'utilization') {
                res = await getMetricByPeriod(machines, shiftParam, from, to, utilView, 'run');
            } else if (tab === 'oee') {
                res = await getMetricByPeriod(machines, shiftParam, from, to, oeeView, 'oee');
            } else if (tab === 'esh_lh') {
                res = await getEshLhByPeriod(machines, shiftParam, from, to, eshLhView);
            }
            if (reqId !== requestIdRef.current) return; // newer call superseded this one
            if (res !== undefined) setChartData(res);
        } catch (err) {
            if (reqId === requestIdRef.current) console.error('[AnalyticsV2] fetch error:', err);
        } finally {
            if (reqId === requestIdRef.current) setIsLoading(false);
        }
    };

    // ── Filter helpers ───────────────────────────────────────────────────────

    const renderPlantFilter = () => showPlantFilter && (
        <PlantFilterSelect plantOptions={plantOptions} selectedPlants={selectedPlants} onChange={handlePlantChange} />
    );

    const renderGroupFilter = () => showMachineGroupsDropdown && (
        <FormControl size="small" className="av2-filter">
            <InputLabel>Machine Group</InputLabel>
            <Select multiple value={selectedGroups} onChange={e => handleGroupChange(e.target.value)} label="Machine Group"
                renderValue={sel => sel.length === machineGroups.length ? 'All Groups' : sel.length === 0 ? 'Select Group' : sel.join(', ')}>
                <MenuItem value="all"><Checkbox checked={selectedGroups.length === machineGroups.length} sx={{ '&.Mui-checked': { color: '#f47803' } }} /><ListItemText primary="All" /></MenuItem>
                {machineGroups.map(g => (
                    <MenuItem key={g.name} value={g.name}><Checkbox checked={selectedGroups.includes(g.name)} sx={{ '&.Mui-checked': { color: '#f47803' } }} /><ListItemText primary={g.name} /></MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    const renderMachineFilter = (single = false) => (
        <FormControl size="small" className="av2-filter">
            <InputLabel>Machine *</InputLabel>
            {single ? (
                <Select value={tab === 'cycle_time' ? cycleMachine : (selectedMachines[0] || '')}
                    onChange={e => tab === 'cycle_time' ? setCycleMachine(e.target.value) : setSelectedMachines(e.target.value ? [e.target.value] : [])}
                    label="Machine *">
                    {availableMachines.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </Select>
            ) : (
                <Select multiple value={selectedMachines} onChange={e => handleMachineChange(e.target.value)} label="Machine *"
                    onClose={() => setMachineDropdownSearch("")}
                    renderValue={sel => isAllMachinesSelected ? 'All Machines' : sel.length === 0 ? 'Select' : sel.slice(0, 1).join(', ') + (sel.length > 1 ? '...' : '')}
                    MenuProps={{ autoFocus: false, PaperProps: { style: { maxHeight: 320 } } }}>
                    <ListSubheader sx={{ p: 0 }}>
                        <TextField
                            size="small"
                            fullWidth
                            autoFocus
                            placeholder="Search machines..."
                            value={machineDropdownSearch}
                            onChange={(e) => setMachineDropdownSearch(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key !== "Escape") e.stopPropagation();
                            }}
                            sx={{ p: 0.5, pl: 1.5 }}
                            InputProps={{
                                sx: { fontSize: "0.8rem" },
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </ListSubheader>
                    <MenuItem value="all"><Checkbox checked={isAllMachinesSelected} sx={{ '&.Mui-checked': { color: '#f47803' } }} /><ListItemText primary="All Machines" /></MenuItem>
                    {availableMachines
                        .filter(m => m.toLowerCase().includes(machineDropdownSearch.toLowerCase()))
                        .map(m => (
                            <MenuItem key={m} value={m}><Checkbox checked={selectedMachines.includes(m)} sx={{ '&.Mui-checked': { color: '#f47803' } }} /><ListItemText primary={m} /></MenuItem>
                        ))}
                    {availableMachines.filter(m => m.toLowerCase().includes(machineDropdownSearch.toLowerCase())).length === 0 && (
                        <MenuItem disabled><ListItemText primary="No machines found" /></MenuItem>
                    )}
                </Select>
            )}
        </FormControl>
    );

    const renderDateRange = () => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker label="From" value={fromDate} onChange={setFromDate} format="DD-MM-YYYY" disableFuture
                slotProps={{ textField: { size: 'small', className: 'av2-filter' } }} />
            <DatePicker label="To" value={toDate} onChange={setToDate} format="DD-MM-YYYY" disableFuture minDate={fromDate}
                slotProps={{ textField: { size: 'small', className: 'av2-filter' } }} />
        </LocalizationProvider>
    );

    const renderShiftFilter = (value, onChange, disabled = false, single = false) => (
        <FormControl size="small" className="av2-filter" disabled={disabled}>
            <InputLabel>Shift</InputLabel>
            {single ? (
                <Select value={value} onChange={e => onChange(e.target.value)} label="Shift">
                    {shifts.map(s => <MenuItem key={s.shift_no} value={s.shift_no}>Shift {s.shift_no}</MenuItem>)}
                </Select>
            ) : (
                <Select multiple value={value} label="Shift"
                    onChange={e => {
                        const v = e.target.value;
                        if (v.includes('all')) {
                            onChange(isAllShiftsSelected ? [] : shifts.map(s => s.shift_no));
                        } else {
                            onChange(v);
                        }
                    }}
                    renderValue={sel => isAllShiftsSelected ? 'All Shifts' : sel.length === 0 ? 'Select Shift' : sel.map(v => `Shift ${v}`).join(', ')}>
                    <MenuItem value="all"><Checkbox checked={isAllShiftsSelected} sx={{ '&.Mui-checked': { color: '#f47803' } }} /><ListItemText primary="All Shifts" /></MenuItem>
                    {shifts.map(s => (
                        <MenuItem key={s.shift_no} value={s.shift_no}>
                            <Checkbox checked={value.includes(s.shift_no)} sx={{ '&.Mui-checked': { color: '#f47803' } }} />
                            <ListItemText primary={`Shift ${s.shift_no}`} />
                        </MenuItem>
                    ))}
                </Select>
            )}
        </FormControl>
    );

    const clearChart = () => setChartData(null);

    const renderParetoFilters = () => <>
        <FormControl size="small" className="av2-filter">
            <InputLabel>Period</InputLabel>
            <Select value={period} onChange={e => { setPeriod(e.target.value); clearChart(); }} label="Period">
                {PERIOD_OPTS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
        </FormControl>
        <FormControl size="small" className="av2-filter">
            <InputLabel>Top N</InputLabel>
            <Select value={topBars} onChange={e => { setTopBars(e.target.value); clearChart(); }} label="Top N">
                {TOP_N_OPTS.map(n => <MenuItem key={n} value={n}>Top {n}</MenuItem>)}
            </Select>
        </FormControl>
        <FormControl size="small" className="av2-filter">
            <InputLabel>Mini Charts</InputLabel>
            <Select value={topCount} onChange={e => { setTopCount(e.target.value); clearChart(); }} label="Mini Charts">
                {[1, 2, 3, 4, 5].map(n => <MenuItem key={n} value={n}>Top {n}</MenuItem>)}
            </Select>
        </FormControl>
    </>;

    const renderFilters = () => {
        switch (tab) {
            case 'oee':
                return <>{renderPlantFilter()}{renderGroupFilter()}{renderMachineFilter()}{renderDateRange()}{renderShiftFilter(selectedShift, setSelectedShift, false)}
                    <FormControl size="small" className="av2-filter"><InputLabel>Period</InputLabel>
                        <Select value={oeeView} onChange={e => { setOeeView(e.target.value); clearChart(); }} label="Period">
                            {VIEW_OPTS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                        </Select>
                    </FormControl></>;
            case 'utilization':
                return <>{renderPlantFilter()}{renderGroupFilter()}{renderMachineFilter()}{renderDateRange()}{renderShiftFilter(selectedShift, setSelectedShift, false)}
                    <FormControl size="small" className="av2-filter"><InputLabel>Period</InputLabel>
                        <Select value={utilView} onChange={e => { setUtilView(e.target.value); clearChart(); }} label="Period">
                            {VIEW_OPTS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                        </Select>
                    </FormControl></>;
            case 'parts':
                return <>{renderPlantFilter()}{renderGroupFilter()}{renderMachineFilter()}
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker label="From" value={partsFromDate} onChange={v => { setPartsFromDate(v); clearChart(); }} format="DD-MM-YYYY" disableFuture
                            slotProps={{ textField: { size: 'small', className: 'av2-filter' } }} />
                        <DatePicker label="To" value={partsToDate} onChange={v => { setPartsToDate(v); clearChart(); }} format="DD-MM-YYYY" disableFuture minDate={partsFromDate}
                            slotProps={{ textField: { size: 'small', className: 'av2-filter' } }} />
                    </LocalizationProvider></>;
            case 'parts_summary':
                return <>{renderPlantFilter()}{renderGroupFilter()}{renderMachineFilter(true)}
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker label="Date" value={summaryDate} onChange={setSummaryDate} format="DD-MM-YYYY" disableFuture
                            slotProps={{ textField: { size: 'small', className: 'av2-filter' } }} />
                    </LocalizationProvider>
                    {renderShiftFilter(summaryShift, setSummaryShift, false, true)}</>;
            case 'cycle_time':
                return <>{renderPlantFilter()}{renderGroupFilter()}{renderMachineFilter(true)}
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker label="Date" value={cycleDate} onChange={setCycleDate} format="DD-MM-YYYY" disableFuture
                            slotProps={{ textField: { size: 'small', className: 'av2-filter' } }} />
                    </LocalizationProvider>
                    {renderShiftFilter(cycleShift, setCycleShift, false, true)}
                    <FormControl size="small" className="av2-filter"><InputLabel>Component</InputLabel>
                        <Select value={cycleComponent} onChange={e => setCycleComponent(e.target.value)} label="Component">
                            <MenuItem value="all">All Components</MenuItem>
                            {availableComponents.map(c => <MenuItem key={c.name} value={c.name}>{c.name} ({c.code})</MenuItem>)}
                        </Select>
                    </FormControl></>;
            case 'downtime':
            case 'alarm':
                return <>{renderPlantFilter()}{renderGroupFilter()}{renderMachineFilter()}{renderDateRange()}{renderShiftFilter(selectedShift, setSelectedShift, isShiftDisabled)}{renderParetoFilters()}</>;
            case 'esh_lh':
                return <>{renderPlantFilter()}{renderGroupFilter()}{renderMachineFilter()}{renderDateRange()}{renderShiftFilter(selectedShift, setSelectedShift, false)}
                    <FormControl size="small" className="av2-filter"><InputLabel>Period</InputLabel>
                        <Select value={eshLhView} onChange={e => { setEshLhView(e.target.value); clearChart(); }} label="Period">
                            {VIEW_OPTS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                        </Select>
                    </FormControl></>;
            default:
                return null;
        }
    };

    // ── Chart builders ───────────────────────────────────────────────────────

    const buildParetoChart = (data, isExpanded, onOthersClick, onReasonClick, label) => {
        const allReasons = (data.reasons || []).filter(r => (r.name || '').toLowerCase() !== 'others');
        const unassignedSecs = data.unassigned_others_seconds || 0;
        const totalSecs = data.total_duration_seconds || (allReasons.reduce((s, r) => s + r.total_duration_seconds, 0) + unassignedSecs);

        // Build display list: top N + single "Others" OR all when expanded
        let displayReasons;
        if (isExpanded) {
            let cum = 0;
            displayReasons = allReasons.map(r => {
                cum += r.total_duration_seconds;
                return { ...r, cumPct: totalSecs > 0 ? +((cum / totalSecs) * 100).toFixed(2) : 0, isOthers: false };
            });
            if (unassignedSecs > 0) {
                cum += unassignedSecs;
                displayReasons.push({
                    name: 'Others',
                    total_duration_seconds: unassignedSecs,
                    cumPct: totalSecs > 0 ? +((cum / totalSecs) * 100).toFixed(2) : 100,
                    isOthers: true,
                });
            }
        } else {
            const topItems = allReasons.slice(0, topBars);
            const restItems = allReasons.slice(topBars);
            let cum = 0;
            displayReasons = topItems.map(r => {
                cum += r.total_duration_seconds;
                return { ...r, cumPct: totalSecs > 0 ? +((cum / totalSecs) * 100).toFixed(2) : 0, isOthers: false };
            });
            const othersSecs = unassignedSecs + restItems.reduce((s, r) => s + r.total_duration_seconds, 0);
            if (othersSecs > 0) {
                cum += othersSecs;
                displayReasons.push({
                    name: 'Others',
                    total_duration_seconds: othersSecs,
                    cumPct: totalSecs > 0 ? +((cum / totalSecs) * 100).toFixed(2) : 100,
                    isOthers: true,
                });
            }
        }

        const barColors = displayReasons.map((r, i) => r.isOthers ? '#9e9e9e' : BAR_COLORS[i % BAR_COLORS.length]);
        // Both series use {x, y} objects so handleFormatXY never receives undefined x
        const barData = displayReasons.map((r, i) => ({
            x: String(r.name ?? ''),
            y: secsToHours(r.total_duration_seconds),
            fillColor: barColors[i],
        }));
        const lineData = displayReasons.map(r => ({
            x: String(r.name ?? ''),
            y: r.cumPct,
        }));
        const secondsData = displayReasons.map(r => ({
            x: String(r.name ?? ''),
            y: r.total_duration_seconds,
        }));
        const hours = barData.map(d => d.y);
        const cumPct = lineData.map(d => d.y);

        const truncLabel = v => v && v.length > 14 ? v.slice(0, 13) + '…' : (v || '');

        const options = {
            chart: {
                type: 'line',
                height: 460,
                width: '100%',
                toolbar: chartToolbar(`${label || 'Pareto'} Analysis`),
                fontFamily: 'inherit',
                animations: { enabled: true, speed: 600 },
                zoom: { enabled: false },
                selection: { enabled: false },
                redrawOnWindowResize: true,
                redrawOnParentResize: true,
                events: {
                    mouseMove: (event, chartCtx, config) => {
                        let idx = config.dataPointIndex;
                        if (idx < 0) {
                            const tx = chartCtx?.w?.globals?.translateX ?? 0;
                            const gridW = chartCtx?.w?.globals?.gridWidth ?? 0;
                            const relX = (event.offsetX || 0) - tx;
                            if (gridW > 0 && relX >= 0 && relX <= gridW)
                                idx = Math.floor((relX / gridW) * displayReasons.length);
                        }
                        const hovered = displayReasons[idx];
                        const clickable = hovered?.isOthers || hovered?.name?.trim().toLowerCase() === 'b/c correction';
                        const el = chartCtx?.el;
                        if (el) el.style.cursor = clickable ? 'pointer' : 'default';
                    },
                    click: (event, chartCtx, config) => {
                        let idx = config.dataPointIndex;
                        if (idx < 0) {
                            const tx = chartCtx?.w?.globals?.translateX ?? 0;
                            const gridW = chartCtx?.w?.globals?.gridWidth ?? 0;
                            const numCats = displayReasons.length;
                            const relX = (event.offsetX || 0) - tx;
                            if (gridW > 0 && relX >= 0 && relX <= gridW)
                                idx = Math.floor((relX / gridW) * numCats);
                        }
                        if (idx < 0 || idx >= displayReasons.length) return;
                        const clicked = displayReasons[idx];
                        if (!clicked) return;
                        if (clicked.isOthers) {
                            onOthersClick();
                        } else if (clicked.name.trim().toLowerCase() === 'b/c correction') {
                            onReasonClick();
                        }
                    },
                },
            },
            plotOptions: {
                bar: { borderRadius: 4, columnWidth: '65%', distributed: false },
            },
            colors: ['#e0e0e0', '#27ae60', 'transparent'],
            stroke: { width: [0, 2.5, 0], curve: 'smooth' },
            markers: {
                size: [0, 5, 0],
                strokeColors: '#fff',
                strokeWidth: 2,
                hover: { size: 7 },
            },
            dataLabels: {
                enabled: true,
                enabledOnSeries: [0],
                formatter: v => `${v} h`,
                style: { fontSize: '10px', fontWeight: 700, colors: ['#333'] },
                offsetY: -16,
                background: { enabled: false },
            },
            xaxis: {
                type: 'category',
                categories: displayReasons.map(r => String(r.name ?? '')),
                labels: {
                    rotate: -40,
                    rotateAlways: true,
                    style: { fontSize: '11px', colors: '#555' },
                    formatter: truncLabel,
                    hideOverlappingLabels: false,
                },
                axisBorder: { show: false },
                axisTicks: { show: false },
                tooltip: { enabled: false },
            },
            yaxis: [
                {
                    seriesName: 'Duration',
                    title: { text: 'Duration (h)', style: { fontSize: '11px', color: '#888' } },
                    labels: { formatter: v => `${v} h`, style: { fontSize: '11px' } },
                    axisBorder: { show: false },
                },
                {
                    seriesName: 'Cumulative %',
                    opposite: true,
                    min: 0,
                    max: 100,
                    tickAmount: 5,
                    labels: { formatter: v => `${v}%`, style: { fontSize: '11px' } },
                    axisBorder: { show: false },
                },
                {
                    seriesName: 'Duration (s)',
                    show: false,
                    labels: { show: false },
                    axisBorder: { show: false },
                    axisTicks: { show: false },
                },
            ],
            legend: { show: false },
            grid: {
                borderColor: '#f0f0f0',
                strokeDashArray: 4,
                xaxis: { lines: { show: false } },
                padding: { top: 32, right: 20, bottom: 0, left: 20 },
            },
            tooltip: {
                shared: true,
                intersect: false,
                custom: ({ dataPointIndex }) => {
                    const item = displayReasons[dataPointIndex] || {};
                    const name = item.name || '';
                    const duration = hours[dataPointIndex] ?? 0;
                    const cum = cumPct[dataPointIndex] ?? 0;
                    const color = barColors[dataPointIndex] || '#999';
                    const hint = item.isOthers ? `<div style="color:#888;font-size:11px;margin-top:4px;padding-top:4px;border-top:1px solid #f0f0f0">Click to expand all reasons</div>` : '';
                    return `<div style="padding:10px 14px;font-size:12px;line-height:1.8;background:#fff;border-radius:8px;box-shadow:0 4px 16px rgba(0,0,0,0.12);min-width:160px">
                        <div style="font-weight:700;color:#1a202c;margin-bottom:4px;border-bottom:1px solid #f0f0f0;padding-bottom:4px">${name}</div>
                        <div style="display:flex;align-items:center;gap:6px">
                            <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color}"></span>
                            <span style="color:#555">Duration:</span>
                            <b style="color:#1a202c">${duration} h</b>
                        </div>
                        <div style="display:flex;align-items:center;gap:6px">
                            <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:#27ae60"></span>
                            <span style="color:#555">Cumulative:</span>
                            <b style="color:#27ae60">${Math.round(cum)}%</b>
                        </div>
                        ${hint}
                    </div>`;
                },
            },
        };

        const series = [
            { name: 'Duration', type: 'bar', data: barData },
            { name: 'Cumulative %', type: 'line', data: lineData },
            { name: 'Duration (s)', type: 'line', data: secondsData },
        ];

        return { options, series };
    };

    const buildMiniChart = (item, color) => {
        const pts = item.data || [];
        const labels = pts.map(d => d.period);
        const hours = pts.map(d => secsToHours(d.duration_seconds));

        return {
            options: {
                chart: {
                    type: 'line',
                    height: 220,
                    toolbar: chartToolbar(item.name || 'Mini Chart'),
                    fontFamily: 'inherit',
                    animations: { enabled: true, speed: 500 },
                    zoom: { enabled: false },
                },
                stroke: { width: 2.5, curve: 'straight' },
                colors: [color || '#f4a72a'],
                markers: {
                    size: 5,
                    colors: [color || '#f4a72a'],
                    strokeColors: '#fff',
                    strokeWidth: 2,
                    hover: { size: 7 },
                },
                dataLabels: {
                    enabled: true,
                    formatter: v => v > 0 ? `${v} h` : '',
                    style: { fontSize: '10px', fontWeight: 700, colors: ['#444'] },
                    offsetY: -10,
                    offsetX: 0,
                    background: { enabled: false },
                },
                xaxis: {
                    type: 'category',
                    categories: labels,
                    labels: { rotate: -30, style: { fontSize: '10px', colors: '#888' } },
                    axisBorder: { show: false },
                    axisTicks: { show: false },
                    tooltip: { enabled: false },
                },
                yaxis: {
                    min: 0,
                    labels: { formatter: v => `${v} h`, style: { fontSize: '10px', colors: '#888' } },
                    title: { text: 'Duration', style: { fontSize: '10px', color: '#aaa' } },
                    axisBorder: { show: false },
                },
                grid: {
                    borderColor: '#f5f5f5',
                    strokeDashArray: 4,
                    xaxis: { lines: { show: false } },
                    padding: { top: 24, right: 16, bottom: 0, left: 25 },
                },
                tooltip: {
                    custom: ({ dataPointIndex }) => {
                        const label = labels[dataPointIndex] || '';
                        const duration = hours[dataPointIndex] ?? 0;
                        return `<div style="padding:8px 12px;font-size:12px;line-height:1.8;background:#fff;border-radius:8px;box-shadow:0 4px 16px rgba(0,0,0,0.12);min-width:140px">
                            <div style="font-weight:700;color:#1a202c;margin-bottom:3px;border-bottom:1px solid #f0f0f0;padding-bottom:3px">${label}</div>
                            <div style="display:flex;align-items:center;gap:6px">
                                <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color}"></span>
                                <span style="color:#555">Duration:</span>
                                <b style="color:#1a202c">${duration} h</b>
                            </div>
                        </div>`;
                    },
                },
            },
            series: [{ name: 'Duration', data: hours }],
        };
    };

    const buildBluecardChart = (bluecard) => {
        const labels = bluecard.map(b => b.reason_name);
        const hours = bluecard.map(b => secsToHours(b.total_duration_seconds));
        const colors = bluecard.map((_, i) => BLUECARD_COLORS[i % BLUECARD_COLORS.length]);
        const truncLabel = v => v && v.length > 16 ? v.slice(0, 15) + '…' : (v || '');

        return {
            options: {
                chart: {
                    type: 'bar',
                    height: 380,
                    toolbar: chartToolbar('BC Correction Sub Reasons'),
                    fontFamily: 'inherit',
                    animations: { enabled: true, speed: 500 },
                    zoom: { enabled: false },
                },
                plotOptions: { bar: { borderRadius: 5, columnWidth: '40%', distributed: true, dataLabels: { position: 'top' } } },
                colors,
                dataLabels: {
                    enabled: true,
                    formatter: (v, { dataPointIndex }) => {
                        const occ = bluecard[dataPointIndex]?.occurrence ?? 0;
                        return [`${occ}`, `${v} h`];
                    },
                    style: { fontSize: '11px', fontWeight: 700, colors: ['#333'] },
                    offsetY: -45,
                    background: { enabled: false },
                },
                xaxis: {
                    type: 'category',
                    categories: labels,
                    labels: {
                        rotate: -30,
                        style: { fontSize: '11px', colors: '#555' },
                        formatter: truncLabel,
                    },
                    axisBorder: { show: false },
                    axisTicks: { show: false },
                    tooltip: { enabled: false },
                },
                yaxis: {
                    labels: { formatter: v => `${v} h`, style: { fontSize: '11px' } },
                    axisBorder: { show: false },
                },
                legend: { show: false },
                grid: {
                    borderColor: '#f0f0f0',
                    strokeDashArray: 4,
                    padding: { top: 48, right: 16, bottom: 0, left: 16 },
                },
                tooltip: {
                    custom: ({ dataPointIndex }) => {
                        const b = bluecard[dataPointIndex] || {};
                        const color = colors[dataPointIndex] || '#999';
                        return `<div style="padding:10px 14px;font-size:12px;line-height:1.8;background:#fff;border-radius:8px;box-shadow:0 4px 16px rgba(0,0,0,0.12);min-width:150px">
                            <div style="font-weight:700;color:#1a202c;margin-bottom:4px;border-bottom:1px solid #f0f0f0;padding-bottom:4px">${b.reason_name || ''}</div>
                            <div style="display:flex;align-items:center;gap:6px">
                                <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color}"></span>
                                <span style="color:#555">Duration:</span>
                                <b>${secsToHours(b.total_duration_seconds ?? 0)} h</b>
                            </div>
                            <div style="display:flex;align-items:center;gap:6px">
                                <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:#1e88e5"></span>
                                <span style="color:#555">Occurrences:</span>
                                <b style="color:#1e88e5">${b.occurrence ?? 0}</b>
                            </div>
                        </div>`;
                    },
                },
            },
            series: [{ name: 'Duration', data: hours }],
        };
    };


    const fmtPeriodLabel = (iso, period) => {
        const d = dayjs(iso); // auto-converts UTC to local time
        if (period === 'week') return `FW-${String(d.isoWeek()).padStart(2, '0')}`;
        if (period === 'month') return d.format('MMM YYYY');
        if (period === 'quarter') return `Q${Math.ceil((d.month() + 1) / 3)} ${d.format('YYYY')}`;
        return d.format('DD-MM-YYYY');
    };

    const buildMachineLineChart = (data, label, period, metricField) => {
        const periodLabel = period === 'week' ? 'Week wise' : period === 'month' ? 'Month wise' : period === 'quarter' ? 'Quarter wise' : 'Day wise';
        const raw = (data.data || []).slice().sort((a, b) => new Date(a.period_start) - new Date(b.period_start));
        const field = metricField || (raw[0] ? Object.keys(raw[0]).find(k => k !== 'period_start') : 'value');

        const fmtLabel = (iso) => fmtPeriodLabel(iso, period);

        const seriesData = raw.map(d => ({
            x: fmtLabel(d.period_start),
            y: +(parseFloat(d[field] ?? 0)).toFixed(1),
        }));

        const series = [{ name: label, data: seriesData }];
        const colors = ['#f47803'];

        const options = {
            chart: {
                type: 'line',
                height: 520,
                toolbar: chartToolbar(`${label} - ${periodLabel}`),
                fontFamily: 'inherit',
                animations: { enabled: true, speed: 500 },
                zoom: { enabled: false },
            },
            stroke: { width: 3, curve: 'smooth' },
            colors,
            markers: { size: 6, strokeColors: '#fff', strokeWidth: 2, hover: { size: 8 } },
            dataLabels: {
                enabled: true,
                formatter: v => `${Math.round(v)}%`,
                style: { fontSize: '11px', fontWeight: 700, colors: ['#333'] },
                offsetY: -15,
                background: { enabled: false },
            },
            xaxis: {
                type: 'category',
                labels: { rotate: -30, style: { fontSize: '11px', colors: '#555' } },
                axisBorder: { show: false },
                axisTicks: { show: false },
                tooltip: { enabled: false },
            },
            yaxis: {
                min: 0,
                max: 100,
                tickAmount: 5,
                labels: {
                    formatter: v => `${v}%`,
                    style: { fontSize: '11px' },
                    minWidth: 48,
                },
                title: { text: label, style: { fontSize: '12px', color: '#888' } },
                axisBorder: { show: false },
            },
            legend: {
                show: true,
                position: 'bottom',
                fontSize: '12px',
                markers: { width: 10, height: 10, radius: 5 },
            },
            grid: {
                borderColor: '#f0f0f0',
                strokeDashArray: 4,
                padding: { top: 36, right: 24, bottom: 0, left: 60 },
            },
            title: {
                text: `${label} — ${periodLabel}`,
                align: 'center',
                style: { fontSize: '14px', fontWeight: 700, color: '#1a202c' },
            },
            tooltip: {
                shared: true,
                intersect: false,
                y: { formatter: v => `${Math.round(v)}%` },
            },
        };

        return { options, series };
    };

    const buildPartsMonthlyChart = (monthly, metricKey, chartTitle, metricLabel) => {
        const rows = (monthly || [])
            .slice()
            .sort((a, b) => new Date(a.month_start) - new Date(b.month_start))
            .map(mo => ({
                label: dayjs(mo.month_start).add(1, 'day').format("MMM 'YY"),
                actual: mo.actual || 0,
                target: mo.tar || 0,
                metric: +(parseFloat(mo[metricKey] ?? 0)).toFixed(1),
            }));

        const totalActual = rows.reduce((s, r) => s + r.actual, 0);
        const totalTarget = rows.reduce((s, r) => s + r.target, 0);
        const totalMetric = totalTarget > 0
            ? +(rows.reduce((s, r) => s + r.metric * r.target, 0) / totalTarget).toFixed(1)
            : 0;
        rows.push({ label: 'Total', actual: totalActual, target: totalTarget, metric: totalMetric });

        const maxBarVal = Math.max(...rows.map(r => Math.max(r.target, r.actual)), 0);

        const firstLabel = rows.length > 1 ? rows[0].label : '';
        const lastLabel = rows.length > 1 ? rows[rows.length - 2].label : '';
        const subtitle = `Actual Vs Target Output (${firstLabel} - ${lastLabel} + Total)`;

        const options = {
            chart: {
                type: 'line', height: 340,
                toolbar: chartToolbar(chartTitle), fontFamily: 'inherit',
                animations: { enabled: true, speed: 500 }, zoom: { enabled: false },
            },
            plotOptions: { bar: { borderRadius: 3, columnWidth: '55%' } },
            colors: ['#3f51b5', '#4caf50', '#e53935'],
            stroke: { width: [0, 0, 2.5], curve: 'smooth' },
            markers: { size: [0, 0, 5], strokeColors: '#fff', strokeWidth: 2, hover: { size: 7 } },
            dataLabels: {
                enabled: true,
                enabledOnSeries: [0, 1, 2],
                formatter: (v, { seriesIndex }) => seriesIndex === 2 ? `${Math.round(v)}%` : `${v}`,
                style: { fontSize: '10px', fontWeight: 700, colors: ['#283593', '#1b5e20', '#c62828'] },
                offsetY: -16,
                background: { enabled: false },
            },
            xaxis: {
                type: 'category',
                labels: { style: { fontSize: '11px', colors: '#555' } },
                axisBorder: { show: false }, axisTicks: { show: false },
                tooltip: { enabled: false },
            },
            yaxis: [
                {
                    seriesName: 'Target',
                    min: 0, max: maxBarVal,
                    title: { text: 'Units', style: { fontSize: '11px', color: '#888' } },
                    labels: { style: { fontSize: '11px' } },
                    axisBorder: { show: false },
                },
                { seriesName: 'Actual', show: false, min: 0, max: maxBarVal },
                {
                    seriesName: metricLabel,
                    opposite: true,
                    min: 0,
                    title: { text: `${metricLabel} %`, style: { fontSize: '11px', color: '#888' } },
                    labels: { formatter: v => `${v}%`, style: { fontSize: '11px' } },
                    axisBorder: { show: false },
                },
            ],
            legend: { show: true, position: 'bottom', fontSize: '12px', markers: { width: 10, height: 10, radius: 5 } },
            grid: { borderColor: '#f0f0f0', strokeDashArray: 4, padding: { top: 44, right: 20, bottom: 0, left: 16 } },
            title: { text: chartTitle, align: 'left', style: { fontSize: '13px', fontWeight: 700, color: '#1a202c' } },
            subtitle: { text: subtitle, align: 'left', style: { fontSize: '11px', color: '#888' } },
            tooltip: {
                shared: true, intersect: false,
                y: [{ formatter: v => `${v}` }, { formatter: v => `${v}` }, { formatter: v => `${Math.round(v)}%` }],
            },
        };

        return {
            options,
            series: [
                { name: 'Target', type: 'bar', data: rows.map(r => ({ x: r.label, y: r.target })) },
                { name: 'Actual', type: 'bar', data: rows.map(r => ({ x: r.label, y: r.actual })) },
                { name: metricLabel, type: 'line', data: rows.map(r => ({ x: r.label, y: r.metric })) },
            ],
        };
    };

    const renderPartsContent = (data) => {
        const calcPeriod = ({ tar, actual } = {}) => {
            const a = actual || 0, t = tar || 0;
            return { actual: a, target: t, difference: a - t, performance: t > 0 ? Math.round((a / t) * 100) : 0 };
        };

        const today = calcPeriod(data.today);
        const yesterday = calcPeriod(data.yesterday);
        const week = calcPeriod(data.thisWeek);
        const todayPct = today.performance;
        const diff = Math.round(todayPct - yesterday.performance);

        const utilChart = buildPartsMonthlyChart(data.monthlyUtilization, 'utilization', 'Monthly Utilization Trend', 'Utilization');
        const perfChart = buildPartsMonthlyChart(data.monthlyEfficiency, 'efficiency', 'Monthly Performance Trend', 'Efficiency');

        const tableRows = [
            { label: 'Today', ...today },
            { label: 'Yesterday', ...yesterday },
            { label: 'This Week', ...week },
        ];

        return (
            <Box sx={{ width: '100%' }}>
                {/* Gauge + Production table */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'stretch' }}>
                    <Box className="av2-chart-card" sx={{ p: 2, minWidth: 190, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 1.5 }}>
                        <Typography sx={{ fontSize: '0.8rem', color: '#888', mb: 1.5 }}>Today Efficiency</Typography>
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: 120, textAlign: 'right' }}>
                                {[100, 80, 60, 40, 20, 0].map(v => (
                                    <Typography key={v} sx={{ fontSize: '9px', color: '#aaa', lineHeight: 1 }}>{v}</Typography>
                                ))}
                            </Box>
                            <Box sx={{ width: 20, height: 120, border: '1.5px solid #ddd', borderRadius: '10px', overflow: 'hidden', background: '#f5f5f5', display: 'flex', alignItems: 'flex-end' }}>
                                <Box sx={{ width: '100%', height: `${Math.min(100, Math.max(0, todayPct))}%`, background: 'linear-gradient(to top, #b71c1c, #e53935)', borderRadius: '8px', transition: 'height 0.6s ease' }} />
                            </Box>
                        </Box>
                        <Typography sx={{ fontSize: '2rem', fontWeight: 800, color: '#e53935', mt: 1.5, lineHeight: 1 }}>{todayPct}%</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: diff >= 0 ? '#43a047' : '#e53935', mt: 0.5 }}>
                            {diff >= 0 ? '↑' : '↓'} {Math.abs(diff)} vs Yesterday
                        </Typography>
                    </Box>

                    <Box className="av2-chart-card" sx={{ flex: 1, p: 2 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', mb: 1.5, color: '#1a202c', ml: 1.5 }}>Production Data</Typography>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead>
                                <tr>
                                    {['METRIC', 'ACTUAL', 'TARGET', 'DIFFERENCE', 'PERFORMANCE'].map(h => (
                                        <th key={h} style={{ textAlign: 'left', padding: '6px 12px', color: '#888', fontWeight: 600, fontSize: '11px', borderBottom: '1px solid #f0f0f0', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {tableRows.map((r, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #f9f9f9' }}>
                                        <td style={{ padding: '10px 12px', color: '#555', fontWeight: 500 }}>{r.label}</td>
                                        <td style={{ padding: '10px 12px', fontWeight: 600, color: '#1a202c' }}>{r.actual}</td>
                                        <td style={{ padding: '10px 12px', fontWeight: 600, color: '#1a202c' }}>{r.target}</td>
                                        <td style={{ padding: '10px 12px', fontWeight: 700, color: r.difference < 0 ? '#e53935' : '#43a047' }}>{r.difference}</td>
                                        <td style={{ padding: '10px 12px', fontWeight: 700, color: '#1a202c' }}>{r.performance}%</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Box>
                </Box>

                {/* Monthly charts */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* GPLAST-only */}
                    {cleanCustomerId(customerId) === envConfig.GPLAST_CUSTOMER_ID && (
                        <Box className="av2-chart-card">
                            <ReactApexChart options={utilChart.options} series={utilChart.series} type="line" height={340} width="100%" />
                        </Box>
                    )}
                    <Box className="av2-chart-card">
                        <ReactApexChart options={perfChart.options} series={perfChart.series} type="line" height={340} width="100%" />
                    </Box>
                </Box>
            </Box>
        );
    };

    // ── HH:MM:SS → seconds ───────────────────────────────────────────────────
    const parseHMS = (hms) => {
        if (!hms || typeof hms !== 'string') return 0;
        const [h = 0, m = 0, s = 0] = hms.split(':').map(Number);
        return h * 3600 + m * 60 + s;
    };

    const fmtSec = (sec) => {
        const s = Math.round(sec);
        if (s >= 3600) {
            const h = Math.floor(s / 3600);
            const m = Math.floor((s % 3600) / 60);
            const rem = s % 60;
            return `${h} hr ${m} min ${rem} s`;
        }
        if (s >= 60) return `${Math.floor(s / 60)} min ${s % 60} s`;
        return `${s} s`;
    };

    // ── Parts Summary chart (Runtime vs Idle Time per part) ──────────────────
    const buildPartsSummaryChart = (rawData) => {
        const fmtTs = ts => ts ? dayjs(ts).format('hh:mm:ss A') : '-';
        const data = Array.isArray(rawData) ? rawData : [];

        // Flatten every individual segment in chronological order
        const allSegments = [];
        data.forEach(d => {
            (d.runtime || []).forEach(s => allSegments.push({ ...s, segType: 'runtime', component: d.component_name, partscount: d.partscount }));
            (d.idletime || []).forEach(s => allSegments.push({ ...s, segType: 'idle', component: d.component_name, partscount: d.partscount }));
        });
        allSegments.sort((a, b) => a.start_time - b.start_time);

        // Use sequential index so bars never merge; label formatter shows partscount
        const runtimeData = allSegments.map((s, i) => ({ x: i, y: s.segType === 'runtime' ? +(s.duration / 1000).toFixed(1) : null }));
        const idleData = allSegments.map((s, i) => ({ x: i, y: s.segType === 'idle' ? +(s.duration / 1000).toFixed(1) : null }));

        return {
            segments: allSegments,
            series: [
                { name: 'Runtime', data: runtimeData },
                { name: 'Idle Time', data: idleData },
            ],
            options: {
                chart: { type: 'bar', height: 420, toolbar: { show: true, tools: { download: true, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true }, export: chartToolbar('Part Wise Summary').export }, fontFamily: 'inherit', zoom: { enabled: true, type: 'x' }, selection: { enabled: true, type: 'x' }, animations: { enabled: false } },
                plotOptions: { bar: { borderRadius: 2, columnWidth: '80%', distributed: false } },
                colors: ['#4caf50', '#f4b942'],
                dataLabels: { enabled: false },
                legend: { show: true, position: 'top', fontSize: '12px', markers: { width: 10, height: 10, radius: 5 } },
                xaxis: {
                    type: 'numeric',
                    title: { text: 'Parts Count', style: { fontSize: '12px', color: '#888' } },
                    labels: { formatter: v => String(allSegments[Math.round(v)]?.partscount ?? Math.round(v)), style: { fontSize: '10px' } },
                    axisBorder: { show: false }, axisTicks: { show: false },
                    tooltip: { enabled: false },
                },
                yaxis: {
                    title: { text: 'Time', style: { fontSize: '12px', color: '#888' } },
                    labels: { formatter: v => v >= 60 ? `${Math.floor(v / 60)} min` : `${v} sec`, style: { fontSize: '11px' } },
                    axisBorder: { show: false },
                },
                grid: { borderColor: '#f0f0f0', strokeDashArray: 4, padding: { top: 16, right: 16, bottom: 0, left: 16 } },
                tooltip: {
                    custom: ({ dataPointIndex }) => {
                        const s = allSegments[dataPointIndex] || {};
                        const color = s.segType === 'runtime' ? '#4caf50' : '#f4b942';
                        const label = s.segType === 'runtime' ? 'Runtime' : 'Idle Time';
                        return `<div style="padding:10px 14px;font-size:12px;line-height:1.9;background:#fff;border-radius:8px;box-shadow:0 4px 16px rgba(0,0,0,0.12);min-width:220px">
                            <div style="font-weight:700;color:${color};margin-bottom:6px">● ${label}</div>
                            ${s.component ? `<div><span style="color:#888">Component:</span> <b>${s.component}</b></div>` : ''}
                            <div><span style="color:#888">Parts Count:</span> <b>${s.partscount ?? 0}</b></div>
                            <div><span style="color:#888">Start:</span> <b>${fmtTs(s.start_time)}</b></div>
                            <div><span style="color:#888">End:</span> <b>${fmtTs(s.end_time)}</b></div>
                            <div><span style="color:#888">Duration:</span> <b>${fmtSec(s.duration / 1000)}</b></div>
                        </div>`;
                    },
                },
            },
        };
    };

    // ── Cycle Time Analysis chart ─────────────────────────────────────────────
    const buildCycleTimeChart = (rawData) => {
        const data = [...rawData].sort((a, b) => (a.partscount_start_time || 0) - (b.partscount_start_time || 0));
        const total = data.length;

        // Group consecutive entries by component name, then merge same-named groups
        // separated only by null/empty gaps
        const rawGroups = [];
        data.forEach((d, i) => {
            const last = rawGroups[rawGroups.length - 1];
            if (!last || last.name !== d.component_name) {
                rawGroups.push({
                    name: d.component_name || '', start: i, end: i,
                    cycleTime: d.component_cycle_time, handlingTime: d.component_handling_time
                });
            } else {
                last.end = i;
            }
        });
        // Merge same-name groups across null gaps so one component doesn't show multiple boxes
        const groups = [];
        rawGroups.forEach(g => {
            if (!g.name) return; // skip null-component gaps
            const last = groups[groups.length - 1];
            if (last && last.name === g.name) {
                last.end = g.end; // extend range
            } else {
                groups.push({ ...g });
            }
        });

        // Per-entry std for stats and exceeds
        const getStd = d => d.component_cycle_time ? parseHMS(d.component_cycle_time) : null;
        const getStdH = d => d.component_handling_time ? parseHMS(d.component_handling_time) : null;

        const fastCycle = data.filter(d => { const s = getStd(d); return s && d.totalrunduration / 1000 < s; }).length;
        const slowCycle = data.filter(d => { const s = getStd(d); return s && d.totalrunduration / 1000 > s; }).length;
        const fastHandle = data.filter(d => { const s = getStdH(d); return s && d.totalidleduration / 1000 < s; }).length;
        const slowHandle = data.filter(d => { const s = getStdH(d); return s && d.totalidleduration / 1000 > s; }).length;

        const cycleVals = data.map(d => +(d.totalrunduration / 1000).toFixed(1));
        const handleVals = data.map(d => +(d.totalidleduration / 1000).toFixed(1));

        const series = [
            { name: 'Cycle Time', type: 'bar', data: data.map((d, i) => ({ x: i, y: cycleVals[i] })) },
            { name: 'Handling Time', type: 'bar', data: data.map((d, i) => ({ x: i, y: handleVals[i] })) },
        ];

        // One std cycle + std handling annotation per unique component group
        const seenStd = new Set();
        const yAnnotations = [];
        groups.forEach(g => {
            const sc = g.cycleTime ? parseHMS(g.cycleTime) : null;
            const sh = g.handlingTime ? parseHMS(g.handlingTime) : null;
            if (sc && !seenStd.has(`c${sc}`)) {
                seenStd.add(`c${sc}`);
                yAnnotations.push({ y: sc, borderColor: '#4caf50', strokeDashArray: 6, borderWidth: 2, label: { text: 'Std Cycle', position: 'right', style: { background: 'transparent', color: '#4caf50', fontSize: '10px' } } });
            }
            if (sh && !seenStd.has(`h${sh}`)) {
                seenStd.add(`h${sh}`);
                yAnnotations.push({ y: sh, borderColor: '#f4b942', strokeDashArray: 6, borderWidth: 2, label: { text: 'Std Handling', position: 'right', style: { background: 'transparent', color: '#f4b942', fontSize: '10px' } } });
            }
        });

        // Exceeds markers — per-entry std
        const pointAnnotations = data.map((d, i) => {
            const s = getStd(d);
            return (s && d.totalrunduration / 1000 > s) ? {
                x: i, y: cycleVals[i],
                marker: { size: 7, fillColor: '#e53935', strokeColor: '#b71c1c', strokeWidth: 1, shape: 'triangle', offsetY: -4 },
            } : null;
        }).filter(Boolean);

        const options = {
            chart: { type: 'bar', height: 420, toolbar: { show: true, tools: { download: true, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true }, export: chartToolbar('Cycle Time Analysis').export }, fontFamily: 'inherit', zoom: { enabled: true, type: 'x' }, selection: { enabled: true, type: 'x' }, animations: { enabled: false } },
            plotOptions: { bar: { borderRadius: 3, columnWidth: '65%', distributed: false } },
            colors: ['#4caf50', '#f4b942'],
            stroke: { show: false },
            dataLabels: { enabled: false },
            xaxis: {
                type: 'numeric',
                title: { text: 'Part Count', style: { fontSize: '12px', color: '#888' } },
                labels: { formatter: v => String(data[Math.round(v)]?.partscount ?? Math.round(v)), style: { fontSize: '10px' } },
                axisBorder: { show: false }, axisTicks: { show: false },
                tooltip: { enabled: false },
            },
            yaxis: {
                title: { text: 'Time', style: { fontSize: '12px', color: '#888' } },
                labels: { formatter: v => v >= 60 ? `${Math.floor(v / 60)} min` : `${v} s`, style: { fontSize: '11px' } },
                axisBorder: { show: false },
            },
            annotations: {
                yaxis: yAnnotations,
                points: pointAnnotations,
                xaxis: groups.length > 1 ? groups.map(g => ({
                    x: g.start - 0.5,
                    x2: g.end + 0.5,
                    fillColor: 'transparent',
                    borderColor: '#bbb',
                    strokeDashArray: 5,
                    borderWidth: 1.5,
                    opacity: 1,
                })) : [],
            },
            legend: { show: false },
            grid: { borderColor: '#f0f0f0', strokeDashArray: 4, padding: { top: 56, right: 16, bottom: 0, left: 16 } },
            tooltip: {
                shared: true, intersect: false,
                custom: ({ dataPointIndex }) => {
                    const d = data[dataPointIndex] || {};
                    const ct = fmtSec(d.totalrunduration / 1000);
                    const ht = fmtSec(d.totalidleduration / 1000);
                    const stdC = getStd(d);
                    const exceeds = stdC && d.totalrunduration / 1000 > stdC;
                    return `<div style="padding:10px 14px;font-size:12px;line-height:1.8;background:#fff;border-radius:8px;box-shadow:0 4px 16px rgba(0,0,0,0.12);min-width:180px">
                        ${d.component_name ? `<div style="font-weight:700;border-bottom:1px solid #f0f0f0;padding-bottom:4px;margin-bottom:6px">${d.component_name}</div>` : ''}
                        <div><span style="color:#888">Parts Count:</span> <b>${d.partscount ?? 0}</b></div>
                        <div><span style="color:#4caf50">●</span> Cycle Time: <b>${ct}</b>${exceeds ? ' <span style="color:#e53935">▲</span>' : ''}</div>
                        <div><span style="color:#f4b942">●</span> Handling Time: <b>${ht}</b></div>
                        ${d.component_cycle_time ? `<div style="color:#888;font-size:11px;margin-top:4px">M: ${d.component_cycle_time}</div>` : ''}
                        ${d.component_handling_time ? `<div style="color:#888;font-size:11px">H: ${d.component_handling_time}</div>` : ''}
                    </div>`;
                },
            },
        };

        return { series, options, stats: { total, fastCycle, slowCycle, fastHandle, slowHandle }, groups, total };
    };

    const buildIdleReasonChart = (data) => {
        const reasons = (data.idleReasonWithPercentage || []).slice().sort((a, b) => b.total_idle_duration - a.total_idle_duration);
        const labels = reasons.map(r => r.name?.trim() || 'Unknown');
        const durs = reasons.map(r => +(r.total_idle_duration / 60).toFixed(1)); // seconds → minutes
        const counts = reasons.map(r => r.count || 0);
        const pcts = reasons.map(r => +(parseFloat(r.percentage)).toFixed(1));
        const colors = reasons.map((_, i) => BAR_COLORS[i % BAR_COLORS.length]);
        const truncL = v => v && v.length > 16 ? v.slice(0, 15) + '…' : (v || '');

        return {
            series: [{ name: 'Idle Duration (min)', data: durs.map((y, i) => ({ x: String(labels[i]), y, fillColor: colors[i] })) }],
            options: {
                chart: { type: 'bar', height: 460, toolbar: chartToolbar('Idle Reason Report'), fontFamily: 'inherit', zoom: { enabled: false }, animations: { enabled: true, speed: 500 } },
                plotOptions: { bar: { borderRadius: 4, columnWidth: '60%', distributed: true, dataLabels: { position: 'top' } } },
                colors,
                dataLabels: { enabled: true, formatter: v => `${v} m`, style: { fontSize: '10px', fontWeight: 700, colors: ['#333'] }, offsetY: -14, background: { enabled: false } },
                xaxis: { type: 'category', labels: { rotate: -35, formatter: truncL, style: { fontSize: '11px', colors: '#555' } }, axisBorder: { show: false }, axisTicks: { show: false }, tooltip: { enabled: false } },
                yaxis: { title: { text: 'Duration (min)', style: { fontSize: '11px', color: '#888' } }, labels: { formatter: v => `${v} m`, style: { fontSize: '11px' } }, axisBorder: { show: false } },
                legend: { show: false },
                grid: { borderColor: '#f0f0f0', strokeDashArray: 4, padding: { top: 28, right: 16, bottom: 0, left: 16 } },
                tooltip: {
                    custom: ({ dataPointIndex }) => {
                        const r = reasons[dataPointIndex] || {};
                        const color = colors[dataPointIndex] || '#999';
                        return `<div style="padding:10px 14px;font-size:12px;line-height:1.8;background:#fff;border-radius:8px;box-shadow:0 4px 16px rgba(0,0,0,0.12);min-width:180px">
                            <div style="font-weight:700;color:#1a202c;border-bottom:1px solid #f0f0f0;padding-bottom:4px;margin-bottom:4px">${r.name?.trim()}</div>
                            <div><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color};margin-right:6px"></span>Duration: <b>${fmtSec(r.total_idle_duration)}</b></div>
                            <div style="color:#555">Count: <b>${r.count}</b></div>
                            <div style="color:#555">Percentage: <b>${Math.round(parseFloat(r.percentage) || 0)}%</b></div>
                        </div>`;
                    },
                },
            },
        };
    };

    const buildEshLhChart = (data) => {
        const periods = data?.periods || [];
        const labels = periods.map(p => p.period_label);
        const eshVals = periods.map(p => +(p.total_esh_hours || 0).toFixed(2));
        const lshVals = periods.map(p => +(p.total_lsh_hours || 0).toFixed(2));
        const effVals = periods.map(p => +(p.efficiency_percent || 0).toFixed(2));
        const maxBar = Math.max(...eshVals, ...lshVals, 1) * 1.3;
        const fmtCycleSeconds = seconds => {
            if (seconds == null || Number.isNaN(Number(seconds))) return '—';
            const value = Number(seconds);
            return value >= 60 ? `${Math.floor(value / 60)}m ${Math.round(value % 60)}s` : `${value.toFixed(1)}s`;
        };
        const cycleTimeSummary = period => {
            const summary = {
                targetTotal: 0, targetParts: 0,
                onTarget: { parts: 0, timeTotal: 0, timeParts: 0 },
                faster: { parts: 0, timeTotal: 0, timeParts: 0 },
                slower: { parts: 0, timeTotal: 0, timeParts: 0 },
            };
            (period?.machines || []).forEach(machine => (machine.components || []).forEach(component => {
                const distribution = component.cycle_time_distribution || {};
                const bucket = (name, key) => {
                    const value = distribution[name] || {};
                    const parts = Number(value.parts_count) || 0;
                    const average = Number(value.avg_cycle_seconds);
                    summary[key].parts += parts;
                    if (parts > 0 && !Number.isNaN(average)) {
                        summary[key].timeTotal += average * parts;
                        summary[key].timeParts += parts;
                    }
                };
                const componentParts = ['on_target', 'faster_than_target', 'slower_than_target']
                    .reduce((total, name) => total + (Number(distribution[name]?.parts_count) || 0), 0);
                const target = Number(component.target_cycle_seconds);
                if (!Number.isNaN(target) && componentParts > 0) {
                    summary.targetTotal += target * componentParts;
                    summary.targetParts += componentParts;
                }
                bucket('on_target', 'onTarget');
                bucket('faster_than_target', 'faster');
                bucket('slower_than_target', 'slower');
            }));
            return summary;
        };
        const formatBucket = bucket => `${bucket.parts} completed${bucket.timeParts ? ` (avg ${fmtCycleSeconds(bucket.timeTotal / bucket.timeParts)})` : ''}`;

        const options = {
            chart: {
                type: 'line', height: 520,
                toolbar: chartToolbar('ESH vs LH'), fontFamily: 'inherit',
                animations: { enabled: true, speed: 500 }, zoom: { enabled: false },
            },
            plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
            colors: ['#3f51b5', '#4caf50', '#e53935'],
            // White border around the bars visually separates the adjacent
            // ESH and LH columns (ApexCharts has no intra-group gap option).
            stroke: { show: true, width: [4, 4, 3], colors: ['#fff', '#fff', '#e53935'], curve: 'smooth' },
            markers: { size: [0, 0, 6], strokeColors: '#fff', strokeWidth: 2, hover: { size: 8 } },
            dataLabels: {
                enabled: true,
                enabledOnSeries: [0, 1, 2],
                formatter: (v, { seriesIndex }) => seriesIndex === 2 ? `${v}` : `${v} h`,
                style: { fontSize: '10px', fontWeight: 700, colors: ['#283593', '#1b5e20', '#000000'] },
                offsetY: -16,
                background: { enabled: false },
            },
            xaxis: {
                type: 'category', categories: labels,
                labels: { rotate: -30, style: { fontSize: '11px', colors: '#555' } },
                axisBorder: { show: false }, axisTicks: { show: false },
                tooltip: { enabled: false },
            },
            yaxis: [
                { seriesName: 'ESH Hours', min: 0, max: maxBar, title: { text: 'Hours', style: { fontSize: '11px', color: '#888' } }, labels: { formatter: v => `${v} h`, style: { fontSize: '11px' } }, axisBorder: { show: false } },
                { seriesName: 'LH Hours', show: false, min: 0, max: maxBar },
                { seriesName: 'Efficiency', opposite: true, min: 0, title: { text: 'Efficiency', style: { fontSize: '11px', color: '#888' } }, labels: { formatter: v => `${v}%`, style: { fontSize: '11px' } }, axisBorder: { show: false } },
            ],
            legend: { show: true, position: 'bottom', fontSize: '12px', markers: { width: 10, height: 10, radius: 5 } },
            grid: { borderColor: '#f0f0f0', strokeDashArray: 4, padding: { top: 36, right: 20, bottom: 0, left: 20 } },
            title: { text: 'ESH vs LH — Efficiency Trend', align: 'center', style: { fontSize: '14px', fontWeight: 700, color: '#1a202c' } },
            tooltip: {
                shared: true, intersect: false,
                custom: ({ dataPointIndex }) => {
                    const period = periods[dataPointIndex] || {};
                    const summary = cycleTimeSummary(period);
                    const target = summary.targetParts ? summary.targetTotal / summary.targetParts : null;
                    return `<div style="padding:10px 14px;font-size:12px;line-height:1.8;background:#fff;border-radius:8px;box-shadow:0 4px 16px rgba(0,0,0,0.12);min-width:230px">
                        <div style="font-weight:700;border-bottom:1px solid #f0f0f0;padding-bottom:4px;margin-bottom:5px">${period.period_label || 'Period'}</div>
                        <div><span style="color:#283593">●</span> ESH Hours: <b>${eshVals[dataPointIndex] ?? 0} h</b></div>
                        <div><span style="color:#2e7d32">●</span> LH Hours: <b>${lshVals[dataPointIndex] ?? 0} h</b></div>
                        <div><span style="color:#e53935">●</span> Efficiency: <b>${effVals[dataPointIndex] ?? 0}%</b></div>
                        <div style="margin-top:5px;padding-top:5px;border-top:1px solid #f0f0f0;font-weight:700;color:#344054">Cycle Time</div>
                        <div><span style="color:#7b1fa2">●</span> Target CT: <b>${fmtCycleSeconds(target)}</b></div>
                        <div><span style="color:#2e7d32">●</span> On-CT: <b>${formatBucket(summary.onTarget)}</b></div>
                        <div><span style="color:#1565c0">●</span> Faster: <b>${formatBucket(summary.faster)}</b></div>
                        <div><span style="color:#e65100">●</span> Slower: <b>${formatBucket(summary.slower)}</b></div>
                    </div>`;
                },
            },
        };

        const series = [
            { name: 'ESH Hours', type: 'bar', data: eshVals },
            { name: 'LH Hours', type: 'bar', data: lshVals },
            { name: 'Efficiency', type: 'line', data: effVals },
        ];

        return { options, series };
    };

    // ESH/LH Trend Analysis — derived from the SAME getEshLhByPeriod response.
    // Surfaces the per-period trend plus the evidence-report drill-down
    // (machines -> components flagged where credited ESH exceeds actual cycle time).
    const renderEshLhTrend = (data, pageInfo = {}) => {
        const periods = data?.periods || [];
        if (!periods.length) return { cardsNode: null, tableNode: null };

        const rows = periods.map((p, pIdx) => {
            const detailMachines = (p.machines || []).filter(m => (m.components || []).length > 0 || (m.esh_hours || 0) > 0);
            let flagged = 0, overcredited = 0, onCt = 0, fasterCt = 0, slowerCt = 0;
            (p.machines || []).forEach(m => (m.components || []).forEach(c => {
                if (c.mismatch_flagged) flagged += 1;
                if ((c.overcredited_hours || 0) > 0) overcredited += c.overcredited_hours;
                const d = c.cycle_time_distribution || {};
                onCt += d.on_target?.parts_count || 0;
                fasterCt += d.faster_than_target?.parts_count || 0;
                slowerCt += d.slower_than_target?.parts_count || 0;
            }));
            return {
                pIdx,
                label: p.period_label,
                esh: +(p.total_esh_hours || 0).toFixed(1),
                lsh: +(p.total_lsh_hours || 0).toFixed(1),
                eff: +(p.efficiency_percent || 0).toFixed(2),
                active: detailMachines.length, flagged,
                overcredited: +overcredited.toFixed(1),
                onCt, fasterCt, slowerCt,
                machines: detailMachines, // for the drill-down
            };
        });

        const totalEsh = +rows.reduce((a, r) => a + r.esh, 0).toFixed(1);
        const totalLsh = +rows.reduce((a, r) => a + r.lsh, 0).toFixed(1);
        const totalEffValue = totalLsh > 0 ? (totalEsh / totalLsh).toFixed(2) : '0.00';
        const cards = [
            { label: 'Total ESH', value: `${totalEsh} h`, color: '#3f51b5' },
            { label: 'Total LH', value: `${totalLsh} h`, color: '#43a047' },
            { label: 'Total ESH/LH Efficiency', value: totalEffValue, color: '#607d8b' },
        ];

        const fmtM = (s) => `${((Number(s) || 0) / 60).toFixed(1)}m`;
        // hex -> rgba, for soft coloured card tints keyed off each metric's colour.
        const hexToRgba = (hex, a) => {
            const h = String(hex).replace('#', '');
            const full = h.length === 3 ? h.split('').map(x => x + x).join('') : h;
            const n = parseInt(full, 16);
            return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
        };
        // Machine-wise, per-component drill-down shown when a period row is expanded.
        const renderDetail = (machines) => (
            <Box sx={{ p: 1.5 }}>
                {(!machines || machines.length === 0) && <Typography sx={{ fontSize: 12, color: '#aaa', p: 1 }}>No machine activity this period.</Typography>}
                {(machines || []).map((m, mi) => (
                    <Box key={mi} sx={{ mb: 1.5, border: '1px solid #eee', borderRadius: 1.5, overflow: 'hidden', background: '#fff' }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', px: 1.5, py: 1, background: '#eef2ff' }}>
                            <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#1a202c' }}>{m.machine_name}</Typography>
                            <Typography sx={{ fontSize: 11, color: '#555' }}>ESH {(m.esh_hours || 0).toFixed(1)}h · LH {(m.lsh_hours || 0).toFixed(1)}h · Eff {Math.round(m.efficiency_percent || 0)}%</Typography>
                            {(m.unique_operators || []).length > 0 && <Typography sx={{ fontSize: 11, color: '#888' }}>Ops: {m.unique_operators.join(', ')}</Typography>}
                        </Box>
                        {(m.components || []).length === 0
                            ? <Typography sx={{ fontSize: 11, color: '#bbb', p: 1 }}>No components.</Typography>
                            : (
                                <Box sx={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                        <thead>
                                            <tr>
                                                {[['Component', 'left'], ['Target CT', 'center'], ['On-CT time', 'center'], ['On-CT parts', 'center'], ['Faster time', 'center'], ['Faster parts', 'center'], ['Slower time', 'center'], ['Slower parts', 'center']].map(([h, a]) => (
                                                    <th key={h} style={{ textAlign: a, padding: '6px 12px', color: '#667085', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '1px solid #eef0f3', whiteSpace: 'nowrap', background: '#fbfcfd' }}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(m.components || []).map((c, ci) => {
                                                const d = c.cycle_time_distribution || {};
                                                const bt = (b) => (b && b.avg_cycle_seconds != null ? fmtM(b.avg_cycle_seconds) : '—');
                                                const buckets = [
                                                    { key: 'onCt', label: 'On-CT parts', color: '#2e7d32', data: d.on_target },
                                                    { key: 'faster', label: 'Faster parts', color: '#1565c0', data: d.faster_than_target },
                                                    { key: 'slower', label: 'Slower parts', color: '#e65100', data: d.slower_than_target },
                                                ];
                                                const rowKey = (b) => `${mi}-${ci}-${b.key}`;
                                                const partsCell = (b) => {
                                                    const count = b.data?.parts_count || 0;
                                                    if (count <= 0) return <td style={{ textAlign: 'center', padding: '7px 12px', color: b.color, fontWeight: 700 }}>—</td>;
                                                    const isOpen = !!partsOpen[rowKey(b)];
                                                    return (
                                                        <td style={{ textAlign: 'center', padding: '7px 12px' }}>
                                                            <span role="button" tabIndex={0}
                                                                onClick={() => setPartsOpen(prev => ({ ...prev, [rowKey(b)]: !prev[rowKey(b)] }))}
                                                                style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3, color: b.color, fontWeight: 700, userSelect: 'none' }}>
                                                                {count}<span style={{ fontSize: 9 }}>{isOpen ? '▾' : '▸'}</span>
                                                            </span>
                                                        </td>
                                                    );
                                                };
                                                // Same visual language as the outer component table — small nested table per bucket.
                                                const partsDetailRow = (b) => {
                                                    const parts = b.data?.parts || [];
                                                    const fmtTs = (ts) => ts ? dayjs(ts).format('DD MMM, hh:mm:ss A') : '—';
                                                    const fmtSec = (s) => {
                                                        if (s == null) return '—';
                                                        const n = Number(s) || 0;
                                                        return n >= 60 ? `${Math.floor(n / 60)}m ${Math.round(n % 60)}s` : `${n.toFixed(1)}s`;
                                                    };
                                                    return (
                                                        <tr key={b.key}>
                                                            <td colSpan={8} style={{ padding: 0, background: hexToRgba(b.color, 0.04), borderBottom: '1px solid #f2f4f7' }}>
                                                                <Box sx={{ p: 1, pl: 3 }}>
                                                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: b.color, mb: 0.5 }}>{c.component_name} · {b.label} ({parts.length})</Typography>
                                                                    {parts.length === 0 ? <Typography sx={{ fontSize: 11, color: '#aaa' }}>No part records.</Typography> : (
                                                                        <Box sx={{ overflowX: 'auto' }}>
                                                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                                                                <thead>
                                                                                    <tr>
                                                                                        {['Start Time', 'End Time', 'Run Time', 'Idle Time', 'Disconnect Time'].map(h => (
                                                                                            <th key={h} style={{ textAlign: 'center', padding: '5px 10px', color: '#667085', fontWeight: 600, fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '1px solid #eef0f3', background: '#fff', whiteSpace: 'nowrap' }}>{h}</th>
                                                                                        ))}
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {parts.map((p, pi) => (
                                                                                        <tr key={pi} style={{ borderBottom: '1px solid #f2f4f7' }}>
                                                                                            <td style={{ textAlign: 'center', padding: '4px 10px', whiteSpace: 'nowrap' }}>{fmtTs(p.start_time)}</td>
                                                                                            <td style={{ textAlign: 'center', padding: '4px 10px', whiteSpace: 'nowrap' }}>{fmtTs(p.end_time)}</td>
                                                                                            <td style={{ textAlign: 'center', padding: '4px 10px', fontWeight: 600, color: '#344054' }}>{fmtSec(p.run_time_seconds)}</td>
                                                                                            <td style={{ textAlign: 'center', padding: '4px 10px', color: '#667085' }}>{fmtSec(p.idle_time_seconds)}</td>
                                                                                            <td style={{ textAlign: 'center', padding: '4px 10px', color: '#667085' }}>{fmtSec(p.disconnect_time_seconds)}</td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </table>
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            </td>
                                                        </tr>
                                                    );
                                                };
                                                const openBuckets = buckets.filter(b => partsOpen[rowKey(b)]);
                                                return (
                                                    <React.Fragment key={ci}>
                                                        <tr style={{ borderBottom: openBuckets.length ? 'none' : '1px solid #f2f4f7' }}>
                                                            <td style={{ textAlign: 'left', padding: '7px 12px', color: '#344054', maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={c.component_name}>{c.component_name}</td>
                                                            <td style={{ textAlign: 'center', padding: '7px 12px', fontWeight: 600, color: '#344054' }}>{fmtM(c.target_cycle_seconds)}</td>
                                                            <td style={{ textAlign: 'center', padding: '7px 12px', color: '#2e7d32' }}>{bt(d.on_target)}</td>
                                                            {partsCell(buckets[0])}
                                                            <td style={{ textAlign: 'center', padding: '7px 12px', color: '#1565c0' }}>{bt(d.faster_than_target)}</td>
                                                            {partsCell(buckets[1])}
                                                            <td style={{ textAlign: 'center', padding: '7px 12px', color: '#e65100' }}>{bt(d.slower_than_target)}</td>
                                                            {partsCell(buckets[2])}
                                                        </tr>
                                                        {openBuckets.map(b => partsDetailRow(b))}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </Box>
                            )}
                    </Box>
                ))}
            </Box>
        );

        const cardsNode = (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'flex-end' }}>
                    {cards.map((c, i) => (
                        <Box key={i} sx={{
                            flex: '0 1 200px', minWidth: 160, maxWidth: 230,
                            position: 'relative',
                            borderRadius: 2,
                            px: 1.75, py: 1.1,
                            background: `linear-gradient(135deg, ${hexToRgba(c.color, 0.13)} 0%, ${hexToRgba(c.color, 0.035)} 100%)`,
                            border: `1px solid ${hexToRgba(c.color, 0.2)}`,
                            borderLeft: `3px solid ${c.color}`,
                            boxShadow: `0 2px 8px ${hexToRgba(c.color, 0.1)}`,
                            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                            '&:hover': { transform: 'translateY(-1px)', boxShadow: `0 4px 12px ${hexToRgba(c.color, 0.16)}` },
                        }}>
                            <Typography sx={{ fontSize: '9.5px', fontWeight: 700, color: '#667085', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.25 }}>{c.label}</Typography>
                            <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: c.color, lineHeight: 1.1 }}>{c.value}</Typography>
                            {c.sub && <Typography sx={{ fontSize: '10px', color: '#8a93a5', mt: 0.25, fontWeight: 500 }}>{c.sub}</Typography>}
                        </Box>
                    ))}
            </Box>
        );

        const tableNode = (
            <Box className="av2-chart-card" sx={{ mt: 2, p: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 2, color: '#1a202c' }}>ESH/LH Trend Analysis</Typography>
                <Box sx={{ overflowX: 'auto', maxHeight: 520, overflowY: 'auto', border: '1px solid #eef0f3', borderRadius: 2 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                            <tr>
                                {[['Period', 'left'], ['ESH (h)', 'center'], ['LH (h)', 'center'], ['Efficiency', 'center'], ['On-CT', 'center'], ['Faster', 'center'], ['Slower', 'center'], ['Detail', 'center']].map(([h, a]) => (
                                    <th key={h} style={{ textAlign: a, padding: '11px 16px', color: '#667085', fontWeight: 600, fontSize: '11px', borderBottom: '1px solid #eef0f3', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap', position: 'sticky', top: 0, background: '#f9fafb', zIndex: 1 }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.slice(pageInfo.startIdx ?? 0, pageInfo.endIdx ?? rows.length).map((r) => {
                                // Auto-expand when only one period is selected so a single
                                // date/machine immediately shows its component breakdown.
                                const open = eshLhExpanded[r.pIdx] ?? (rows.length === 1);
                                return (
                                    <React.Fragment key={r.pIdx}>
                                        <tr style={{ borderBottom: '1px solid #f2f4f7', background: open ? '#fff8f0' : 'transparent' }}>
                                            <td style={{ textAlign: 'left', padding: '11px 16px', color: '#344054', fontWeight: 600, whiteSpace: 'nowrap' }}>{r.label}</td>
                                            <td style={{ textAlign: 'center', padding: '11px 16px', fontWeight: 600, color: '#1a202c' }}>{r.esh}</td>
                                            <td style={{ textAlign: 'center', padding: '11px 16px', fontWeight: 600, color: '#1a202c' }}>{r.lsh}</td>
                                            <td style={{ textAlign: 'center', padding: '11px 16px', fontWeight: 700, color: r.eff >= 85 ? '#43a047' : r.eff >= 60 ? '#f9a825' : '#e53935' }}>{r.eff}</td>
                                            <td style={{ textAlign: 'center', padding: '11px 16px', fontWeight: 700, color: '#2e7d32' }}>{r.onCt}</td>
                                            <td style={{ textAlign: 'center', padding: '11px 16px', fontWeight: 700, color: '#1565c0' }}>{r.fasterCt}</td>
                                            <td style={{ textAlign: 'center', padding: '11px 16px', fontWeight: 700, color: '#e65100' }}>{r.slowerCt}</td>
                                            <td style={{ textAlign: 'center', padding: '8px 16px' }}>
                                                <button type="button"
                                                    onClick={() => setEshLhExpanded(prev => ({ ...prev, [r.pIdx]: !prev[r.pIdx] }))}
                                                    style={{ cursor: 'pointer', border: '1px solid #f47803', color: open ? '#fff' : '#f47803', background: open ? '#f47803' : 'transparent', borderRadius: 4, padding: '4px 12px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                    {open ? 'Hide ▾' : 'View ▸'}
                                                </button>
                                            </td>
                                        </tr>
                                        {open && (
                                            <tr>
                                                <td colSpan={8} style={{ padding: 0, background: '#fafafa', borderBottom: '2px solid #f0f0f0' }}>
                                                    {renderDetail(r.machines)}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </Box>
            </Box>
        );

        return { cardsNode, tableNode };
    };

    const renderChartArea = () => {
        if (isLoading) {
            const ChartSkeleton = ({ height = 520 }) => (
                <Box className="av2-chart-card" sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Skeleton variant="text" width={200} height={22} animation="wave" />
                        <Skeleton variant="circular" width={26} height={26} animation="wave" />
                    </Box>
                    <Skeleton variant="rounded" width="100%" height={height} animation="wave" sx={{ borderRadius: 2 }} />
                    <Skeleton variant="text" width="40%" height={18} animation="wave" sx={{ mx: 'auto', mt: 1.5 }} />
                </Box>
            );
            return (
                <Box sx={{ width: '100%' }}>
                    {tab === 'parts' ? (
                        <>
                            <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                                <Skeleton variant="rounded" width={220} height={220} animation="wave" sx={{ borderRadius: 3 }} />
                                <Skeleton variant="rounded" sx={{ flex: 1, height: 220, borderRadius: 3 }} animation="wave" />
                            </Box>
                            <Box sx={{ mb: 2 }}><ChartSkeleton height={280} /></Box>
                            <ChartSkeleton height={280} />
                        </>
                    ) : (
                        <ChartSkeleton />
                    )}
                </Box>
            );
        }

        if (iframeUrl && ['parts_summary', 'cycle_time'].includes(tab)) {
            return (
                <Box sx={{ width: '100%', height: '70vh' }}>
                    <iframe src={iframeUrl} title={tab} style={{ width: '100%', height: '100%', border: 'none', borderRadius: 12 }} />
                </Box>
            );
        }

        if (chartData && tab === 'parts') return renderPartsContent(chartData);

        if (!chartData && ['downtime', 'alarm'].includes(tab)) return (
            <Typography sx={{ color: '#aaa', fontSize: '0.95rem' }}>
                Select filters and click <b>Run Analysis</b> to load the <b>{TABS.find(t => t.value === tab)?.label}</b> chart.
            </Typography>
        );

        if (chartData && ['downtime', 'alarm'].includes(tab)) {
            const label = tab === 'downtime' ? 'Downtime' : 'Alarm';
            const onReasonClick = () => setSelectedReason(prev => prev === 'bluecard' ? null : 'bluecard');
            const pareto = buildParetoChart(chartData, expandedOthers, () => setExpandedOthers(true), onReasonClick, label);
            const breakdown = (chartData.breakdown || []).slice(0, topCount);
            const totalHours = secsToHours(chartData.total_duration_seconds || 0);

            return (
                <Box sx={{ width: '100%' }}>
                    {/* Total header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1a202c', letterSpacing: 0.2 }}>
                            Total {label} Duration:{' '}
                            <Box component="span" sx={{ color: '#e53935', fontWeight: 800 }}>{totalHours} hours</Box>
                        </Typography>
                        {expandedOthers && (
                            <Button size="small" variant="outlined"
                                onClick={() => setExpandedOthers(false)}
                                sx={{
                                    textTransform: 'none', fontSize: '0.78rem', borderColor: '#bbb', color: '#555',
                                    '&:hover': { borderColor: '#f47803', color: '#f47803' }
                                }}>
                                ▲ Show Top {topBars}
                            </Button>
                        )}
                    </Box>

                    {/* Main chart OR Bluecard chart (only this area swaps) */}
                    <Box sx={{ mb: 3 }}>
                        {selectedReason === 'bluecard' && chartData.bluecard?.length > 0 ? (() => {
                            const bc = buildBluecardChart(chartData.bluecard);
                            return (
                                <Box>
                                    {/* Blue header */}
                                    <Box sx={{
                                        background: 'linear-gradient(135deg,#1565c0 0%,#1e88e5 100%)',
                                        borderRadius: '12px 12px 0 0', px: 3, py: 1.5,
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                                    }}>
                                        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#fff' }}>
                                            B/C Correction — Sub Reasons
                                        </Typography>
                                        <Button size="small" onClick={() => setSelectedReason(null)}
                                            sx={{
                                                color: 'rgba(255,255,255,0.8)', textTransform: 'none', fontSize: '0.78rem',
                                                minWidth: 0, '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.12)' }
                                            }}>
                                            ← Back
                                        </Button>
                                    </Box>
                                    <Box className="av2-chart-card" sx={{ borderRadius: '0 0 12px 12px', pt: 0 }}>
                                        <ReactApexChart
                                            options={bc.options}
                                            series={bc.series}
                                            type="bar"
                                            height={380}
                                            width="100%"
                                        />
                                    </Box>
                                </Box>
                            );
                        })() : (
                            <Box className="av2-chart-card">
                                <ReactApexChart
                                    options={pareto.options}
                                    series={pareto.series}
                                    type="line"
                                    height={460}
                                    width="100%"
                                />
                            </Box>
                        )}
                    </Box>

                    {/* Mini Line Charts — always visible */}
                    {breakdown.length > 0 && (
                        <Box className="av2-mini-grid">
                            {breakdown.map((item, i) => {
                                const color = BAR_COLORS[i % BAR_COLORS.length];
                                const mini = buildMiniChart(item, color);
                                return (
                                    <Box key={i} className="av2-chart-card">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, px: 1, pt: 1 }}>
                                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
                                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1a202c' }}>
                                                TOP {item.rank} — {item.name}
                                            </Typography>
                                            <Box sx={{ ml: 'auto', fontSize: '0.78rem', fontWeight: 600, color, background: `${color}18`, px: 1, py: 0.3, borderRadius: 1 }}>
                                                {formatH(item.total_duration_seconds)}
                                            </Box>
                                        </Box>
                                        <ReactApexChart
                                            options={mini.options}
                                            series={mini.series}
                                            type="line"
                                            height={220}
                                            width="100%"
                                        />
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            );
        }

        if (chartData && tab === 'oee') {
            const chart = buildMachineLineChart(chartData, 'OEE', oeeView, 'oee');
            return (
                <Box sx={{ width: '100%' }}>
                    <Box className="av2-chart-card">
                        <ReactApexChart options={chart.options} series={chart.series} type="line" height={520} width="100%" />
                    </Box>
                </Box>
            );
        }

        if (chartData && tab === 'utilization') {
            const chart = buildMachineLineChart(chartData, 'Utilization', utilView, 'utilization');
            return (
                <Box sx={{ width: '100%' }}>
                    <Box className="av2-chart-card">
                        <ReactApexChart options={chart.options} series={chart.series} type="line" height={520} width="100%" />
                    </Box>
                </Box>
            );
        }

        if (chartData && tab === 'esh_lh') {
            const allPeriods = chartData.periods || [];
            const pageCount = Math.max(1, Math.ceil(allPeriods.length / ESH_PERIODS_PER_PAGE));
            const pageIdx = Math.min(eshLhPage, pageCount - 1);
            const startIdx = pageIdx * ESH_PERIODS_PER_PAGE;
            const endIdx = Math.min(startIdx + ESH_PERIODS_PER_PAGE, allPeriods.length);
            const chart = buildEshLhChart({ ...chartData, periods: allPeriods.slice(startIdx, endIdx) });
            const navBtn = (disabled, onClick, label) => (
                <button type="button" disabled={disabled} onClick={onClick}
                    style={{ cursor: disabled ? 'default' : 'pointer', border: '1px solid #e4e7ec', background: '#fff', color: disabled ? '#cbd2dc' : '#344054', borderRadius: 6, width: 30, height: 30, fontSize: 16, lineHeight: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{label}</button>
            );
            const pager = allPeriods.length > ESH_PERIODS_PER_PAGE ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.25, mt: 1.25, fontSize: 13 }}>
                    <span style={{ color: '#667085' }}>{startIdx + 1}–{endIdx} of {allPeriods.length}</span>
                    {navBtn(pageIdx === 0, () => setEshLhPage(p => Math.max(0, p - 1)), '‹')}
                    {navBtn(pageIdx >= pageCount - 1, () => setEshLhPage(p => Math.min(pageCount - 1, p + 1)), '›')}
                </Box>
            ) : null;
            const trend = renderEshLhTrend(chartData, { startIdx, endIdx });
            return (
                <Box sx={{ width: '100%' }}>
                    {trend.cardsNode}
                    <Box className="av2-chart-card" sx={{ mt: 2 }}>
                        <ReactApexChart options={chart.options} series={chart.series} type="line" height={520} width="100%" />
                        {pager}
                    </Box>
                    {trend.tableNode}
                </Box>
            );
        }

        return (
            <Typography sx={{ color: '#aaa', fontSize: '0.95rem' }}>
                Select filters and click <b>Run Analysis</b> to load the <b>{TABS.find(t => t.value === tab)?.label}</b> chart.
            </Typography>
        );
    };

    return (
        <Box className="av2-root">
            <Box className="av2-tabs-bar">
                <Tabs value={tab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto"
                    TabIndicatorProps={{ sx: { backgroundColor: '#f47803', height: 3 } }}
                    sx={{
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', minWidth: 110 },
                        '& .Mui-selected': { color: '#f47803 !important' }
                    }}>
                    {TABS.map(t => <Tab key={t.value} label={t.label} value={t.value} />)}
                </Tabs>
            </Box>

            <Box className="av2-filter-bar">
                {renderFilters()}
                <Tooltip title={isRunDisabled && !isLoading ? 'Select at least one machine' : ''}>
                    <span>
                        <Button variant="contained" onClick={handleRunAnalysis} disabled={isRunDisabled} className="av2-run-btn">
                            {isLoading ? <><CircularProgress size={16} sx={{ mr: 1, color: '#fff' }} />Analysing...</> : 'Run Analysis'}
                        </Button>
                    </span>
                </Tooltip>
            </Box>

            <Box className="av2-chart-area">
                {renderChartArea()}
            </Box>
        </Box>
    );
}
